import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Header.scss';
import { FaBars, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getLocalizedPath } from '../i18n';
import AppCtaLink from './AppCtaLink';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const langDropdownRef = useRef(null);
  const menuRef = useRef(null);
  const reduceMotion = useReducedMotion();
  
  const languages = [
    { code: 'uk', name: 'UA', flag: '/assets/flags/ukraine.svg' },
    { code: 'en', name: 'EN', flag: '/assets/flags/uk.svg' },
    { code: 'de', name: 'DE', flag: '/assets/flags/germany.svg' },
    { code: 'lt', name: 'LT', flag: '/assets/flags/lithuania.svg' },
  ];
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Check if device is mobile
  useEffect(() => {
    // Initialize isMobile state
    setIsMobile(window.innerWidth <= 1024);
    
    const handleResize = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      
      if (!mobile && menuOpen) {
        setMenuOpen(false);
        document.body.classList.remove('menu-open');
        document.body.style.overflow = '';
      }
    };
    
    // Call once to initialize
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen]);
  
  // Handle body overflow when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('menu-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  
  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
    document.body.classList.remove('menu-open');
    document.body.style.overflow = '';
  }, [location.pathname]);
  
  const closeMenu = () => {
    setMenuOpen(false);
  };

  const toggleMenu = () => {
    setMenuOpen((open) => !open);
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const toggleLangDropdown = (event) => {
    event?.stopPropagation?.();
    setLangDropdownOpen((open) => !open);
  };

  const changeLanguage = (lng) => {
    const currentPath = location.pathname;
    const newPath = getLocalizedPath(currentPath, lng);
    i18n.changeLanguage(lng);
    navigate(newPath);
    setLangDropdownOpen(false);
  };

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === language) || languages[0];
  };

  // Функція для створення локалізованого посилання
  const getLocalizedLink = (path) => {
    return `/${language}${path === '/' ? '' : path}`;
  };

  const navItems = [
    { to: getLocalizedLink('/'), label: t('nav.home'), active: location.pathname === `/${language}` },
    { to: getLocalizedLink('/news'), label: t('nav.news'), active: location.pathname === `/${language}/news` },
    { to: getLocalizedLink('/videos'), label: t('nav.videos'), active: location.pathname === `/${language}/videos` },
    { to: getLocalizedLink('/corporate'), label: t('nav.corporate'), active: location.pathname === `/${language}/corporate`, long: i18n.language === 'de' || i18n.language === 'lt' },
    { to: getLocalizedLink('/ngo'), label: t('nav.ngo'), active: location.pathname === `/${language}/ngo`, long: i18n.language === 'de' || i18n.language === 'lt' },
    // Webinars kept as a route/page, but hidden from the top nav so the bar does not overflow.
    { to: getLocalizedLink('/for-specialists'), label: t('nav.for_specialists', 'For specialists'), active: location.pathname === `/${language}/for-specialists` }
  ];

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''} ${isMobile ? 'mobile' : ''} ${menuOpen ? 'menu-open' : ''}`}>
      <div className="container">
        <div className="header-content">
          <button
            type="button"
            className={`menu-toggle${menuOpen ? ' is-open' : ''}`}
            onClick={toggleMenu}
            aria-label={menuOpen ? t('nav.close_menu', 'Close menu') : t('nav.open_menu', 'Open menu')}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            {menuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>
          
          <div className="logo">
            <motion.div whileHover={reduceMotion ? undefined : { rotate: 6, scale: 1.04 }} transition={{ duration: 0.28 }}>
              <Link to={getLocalizedLink('/')}>
                <img src="/assets/pic/ai-psy-help-logo-f1.png" alt="AI PSY HELP" />
              </Link>
            </motion.div>
          </div>
          
          {!isMobile ? (
            <nav className="main-nav">
              <ul className="nav-links">
                {navItems.map((item) => (
                  <motion.li
                    key={item.to}
                    className={item.long ? 'long-text' : ''}
                    whileHover={reduceMotion ? undefined : { scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link to={item.to} className={item.active ? 'active' : ''}>{item.label}</Link>
                  </motion.li>
                ))}
              </ul>
            </nav>
          ) : null}
          
          {!isMobile && (
            <div className="header-actions">
              <div className="lang-switcher" ref={langDropdownRef}>
                <button 
                  className="lang-current" 
                  onClick={toggleLangDropdown}
                  aria-label={`Change language from ${getCurrentLanguage().name}`}
                  aria-expanded={langDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <div className="lang-current-content">
                    <img 
                      src={getCurrentLanguage().flag} 
                      alt={getCurrentLanguage().name} 
                      className="flag-icon"
                    />
                    <span>{getCurrentLanguage().name}</span>
                  </div>
                  <div className="dropdown-icon-wrapper">
                    {langDropdownOpen ? <FaChevronUp className="dropdown-icon" /> : <FaChevronDown className="dropdown-icon" />}
                  </div>
                </button>
                
                {langDropdownOpen && (
                  <div className="lang-dropdown">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`lang-option ${language === lang.code ? 'active' : ''}`}
                        onClick={() => changeLanguage(lang.code)}
                      >
                        <div className="lang-option-content">
                          <img src={lang.flag} alt={lang.name} className="flag-icon" />
                          <span>{lang.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="auth-buttons">
                <AppCtaLink platform="web" placement="header_desktop" className="btn btn-ghost nav-sign-in">
                  {t('nav.login')}
                </AppCtaLink>
              </div>
            </div>
          )}
        </div>
      </div>
      <AnimatePresence>
        {isMobile && menuOpen ? (
          <motion.div
            className="mobile-menu open"
            id="mobile-menu"
            key="mobile-menu"
            ref={menuRef}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.22 }}
            onClick={closeMenu}
          >
            <button
              type="button"
              className="mobile-menu-close"
              onClick={closeMenu}
              aria-label={t('nav.close_menu', 'Close menu')}
            >
              <FaTimes aria-hidden="true" />
            </button>
            <div className="mobile-menu-content" onClick={(event) => event.stopPropagation()}>
              <ul className="mobile-nav-links">
                {navItems.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} className={item.active ? 'active' : ''}>{item.label}</Link>
                  </li>
                ))}
              </ul>
              <div className="mobile-menu-extra">
                <div className="mobile-languages">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`mobile-lang-option ${language === lang.code ? 'active' : ''}`}
                      onClick={() => changeLanguage(lang.code)}
                    >
                      <img src={lang.flag} alt={lang.name} className="flag-icon" />
                    </button>
                  ))}
                </div>
                <AppCtaLink platform="web" placement="header_mobile" className="mobile-login-btn">
                  {t('nav.login')}
                </AppCtaLink>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
};

export default Header; 